import { useState } from 'react'
import { CheckCircle2, AlertTriangle, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toApiProblem } from '@/lib/api/client'
import { useSettingsMutations } from '../api'
import type { TestEmailResult } from '../types'

/**
 * "Send test email", and what happened, on the line underneath.
 *
 * <p>Sent to whoever pressed it: the person setting up a mail server is the one with an inbox
 * to check. The answer comes back within the server's timeout and says what to fix — a wrong
 * password, a closed port — in the words the person configuring it would use.
 */
export function TestEmailButton() {
  const { testEmail } = useSettingsMutations()
  const [result, setResult] = useState<TestEmailResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-1">
      <Button
        type="button" variant="outline" size="sm"
        disabled={testEmail.isPending}
        onClick={() => {
          setResult(null)
          setError(null)
          testEmail.mutate(null, {
            onSuccess: setResult,
            onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The test could not be run.'),
          })
        }}
      >
        <Send className="size-4" aria-hidden="true" />
        {testEmail.isPending ? 'Sending…' : 'Send test email'}
      </Button>
      {result && (
        <p
          role="status"
          className={`flex items-start gap-1 text-xs ${result.sent ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}
        >
          {result.sent
            ? <CheckCircle2 className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
            : <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />}
          <span>
            {result.message}
            {result.sent && result.sentVia === 'PLATFORM' && ' Sent by the HUMIS default sender.'}
          </span>
        </p>
      )}
      {error && <p role="alert" className="text-xs text-[var(--color-danger)]">{error}</p>}
    </div>
  )
}
